import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, X, Mail, ExternalLink, Building2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import GymCreateForm from './GymCreateForm';
import { uploadGymLogoFile } from './GymLogoUploadField';

function requestToInitialValues(request) {
  return {
    name: request.gym_name || '',
    email: request.email || '',
    phone: request.phone || '',
    address: request.address || '',
    owner_name: request.owner_name || '',
    owner_email: request.owner_email || request.email || '',
    max_users: 5,
    max_trainers: 2,
    plan: 'monthly',
    plan_active: true,
  };
}

function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div>
      <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-sm text-gray-900 dark:text-gray-100 break-words">{value}</dd>
    </div>
  );
}

const GymRequestsManager = ({ onPendingCountChange }) => {
  const { t } = useTranslation();
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();

  const [requests, setRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [approveRequest, setApproveRequest] = useState(null);
  const [createError, setCreateError] = useState(null);
  const [createdCredentials, setCreatedCredentials] = useState(null);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await api.get('/gym_requests.php', { params });
      const list = response.data.requests || [];
      setRequests(list);
      const pending = response.data.pending_count ?? list.filter((r) => r.status === 'pending').length;
      setPendingCount(pending);
      onPendingCountChange?.(pending);
      setError(null);
    } catch (err) {
      console.error(err);
      setError(t('admin.gymRequests.loadError'));
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter, t, onPendingCountChange]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const updateRequestStatus = async (requestId, status, extra = {}) => {
    await api.post('/update_gym_request.php', {
      requestId,
      status,
      ...extra,
    });
    await fetchRequests();
  };

  const handleReject = async (request) => {
    const notes = window.prompt(t('admin.gymRequests.rejectNotesPlaceholder'));
    if (notes === null) return;
    try {
      await updateRequestStatus(request.id, 'rejected', { admin_notes: notes || null });
    } catch (err) {
      setError(t('admin.gymRequests.updateError'));
    }
  };

  const handleCreateFromRequest = async (gymData, logoFile) => {
    if (!approveRequest) return;
    setCreateError(null);
    const payload = {
      ...gymData,
      create_owner: true,
      owner_email: gymData.owner_email || approveRequest.email,
    };
    let res;
    try {
      res = await api.post('/gyms.php', payload);
    } catch (err) {
      const apiErr =
        err.response?.data?.error
        || (typeof err.response?.data === 'string' && err.response.data.includes('insert_gym_record')
          ? 'File gym_billing_schema.php non aggiornato sul server'
          : null)
        || err.message;
      throw new Error(apiErr || t('admin.gymRequests.createError'));
    }
    const gymId = res.data?.gym?.id;
    if (!gymId) {
      throw new Error(res.data?.error || t('admin.gymRequests.createError'));
    }
    if (gymId && logoFile) {
      try {
        await uploadGymLogoFile(api, gymId, logoFile);
      } catch (logoErr) {
        console.error(logoErr);
      }
    }
    await updateRequestStatus(approveRequest.id, 'approved', { gym_id: gymId });
    setCreatedCredentials({
      username: res.data?.owner?.username,
      password: gymData.owner_password,
      gymId,
      emailSent: res.data?.email_sent,
      emailError: res.data?.email_error,
      trialEndsAt: res.data?.trial_ends_at,
    });
    setApproveRequest(null);
  };

  const statusLabel = (status) => {
    if (status === 'pending') return t('admin.gymRequests.statusPending');
    if (status === 'approved') return t('admin.gymRequests.statusApproved');
    return t('admin.gymRequests.statusRejected');
  };

  const statusClass = (status) => {
    if (status === 'pending') return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
    if (status === 'approved') return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
    return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
  };

  if (loading && requests.length === 0) {
    return (
      <div className="flex justify-center items-center h-40">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
        <div className="flex flex-wrap gap-2">
          {['pending', 'approved', 'rejected', ''].map((value) => (
            <button
              key={value || 'all'}
              type="button"
              onClick={() => setStatusFilter(value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                statusFilter === value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {value === '' ? t('admin.gymRequests.filterAll') : statusLabel(value)}
              {value === 'pending' && pendingCount > 0 ? ` (${pendingCount})` : ''}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg">{error}</div>
      )}

      {createdCredentials?.username && (
        <div className="p-4 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-lg text-sm">
          <p className="font-medium text-green-900 dark:text-green-200">{t('admin.gymManager.createSuccess')}</p>
          {createdCredentials.trialEndsAt && (
            <p className="mt-1 text-green-800 dark:text-green-300">
              {t('admin.gymRequests.trialEndsLabel', {
                date: new Date(createdCredentials.trialEndsAt).toLocaleDateString(),
              })}
            </p>
          )}
          {createdCredentials.emailSent ? (
            <p className="mt-2 text-green-800 dark:text-green-300">{t('admin.gymRequests.emailSentSuccess')}</p>
          ) : createdCredentials.emailError ? (
            <p className="mt-2 text-amber-800 dark:text-amber-200">
              {t('admin.gymRequests.emailSentFailed', { error: createdCredentials.emailError })}
            </p>
          ) : null}
          <div className="mt-3 rounded-md border border-green-300/60 dark:border-green-600/50 bg-white/90 dark:bg-gray-950/80 px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100">
            <p className="font-mono">
              <span className="text-gray-600 dark:text-gray-400 not-italic font-sans text-xs uppercase tracking-wide">
                {t('admin.gymManager.fieldUsername')}
              </span>
              <br />
              <span className="font-semibold text-gray-900 dark:text-gray-100">{createdCredentials.username}</span>
            </p>
            <p className="mt-2 font-mono">
              <span className="text-gray-600 dark:text-gray-400 not-italic font-sans text-xs uppercase tracking-wide">
                {t('admin.gymManager.fieldPassword')}
              </span>
              <br />
              <span className="font-semibold break-all text-gray-900 dark:text-gray-100">{createdCredentials.password}</span>
            </p>
          </div>
          {createdCredentials.gymId && (
            <Link
              to={`/admin/gyms/${createdCredentials.gymId}`}
              className="inline-flex items-center gap-1 mt-2 text-blue-600 dark:text-blue-400 hover:underline"
            >
              <ExternalLink className="h-4 w-4" />
              {t('admin.gymRequests.viewGym')}
            </Link>
          )}
        </div>
      )}

      {requests.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700">
          <Building2 className="h-10 w-10 mx-auto mb-2 opacity-50" />
          <p>{t('admin.gymRequests.empty')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <article
              key={request.id}
              className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg overflow-hidden shadow-sm"
            >
              <div className="p-5">
                <div className="flex flex-wrap justify-between gap-3 items-start">
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 dark:text-white">{request.gym_name}</h3>
                    <a
                      href={`mailto:${request.email}`}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1 mt-1"
                    >
                      <Mail className="h-4 w-4" />
                      {request.email}
                    </a>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusClass(request.status)}`}>
                    {statusLabel(request.status)}
                  </span>
                </div>

                <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <DetailRow label={t('admin.gymManager.fieldManagerName')} value={request.owner_name} />
                  <DetailRow label={t('admin.gymManager.fieldPhone')} value={request.phone || request.owner_phone} />
                  <DetailRow label={t('gymRequest.ownerEmail')} value={request.owner_email} />
                  <DetailRow
                    label={t('admin.gymManager.fieldAddress')}
                    value={[request.address, request.city].filter(Boolean).join(', ')}
                  />
                  <DetailRow label={t('admin.gymRequests.vatNumber')} value={request.vat_number} />
                  <DetailRow
                    label={t('admin.gymRequests.estimatedMembers')}
                    value={request.estimated_members != null ? String(request.estimated_members) : null}
                  />
                </dl>

                {request.additional_info && (
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded text-sm">
                    <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.gymRequests.sectionNotes')}</p>
                    <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{request.additional_info}</p>
                  </div>
                )}

                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                  {t('admin.gymRequests.submittedAt')}: {new Date(request.created_at).toLocaleString()}
                  {request.processed_at && (
                    <>
                      {' · '}
                      {t('admin.gymRequests.processedAt')}: {new Date(request.processed_at).toLocaleString()}
                    </>
                  )}
                </p>

                {request.gym_id && (
                  <Link
                    to={`/admin/gyms/${request.gym_id}`}
                    className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {t('admin.gymRequests.linkedGym', { id: request.gym_id })}
                  </Link>
                )}

                {request.status === 'pending' && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setCreateError(null);
                        setApproveRequest(request);
                      }}
                      className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      {t('admin.gymRequests.approveCreate')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleReject(request)}
                      className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
                    >
                      <X className="h-4 w-4 mr-1" />
                      {t('admin.gymRequests.reject')}
                    </button>
                    <a
                      href={`mailto:${request.owner_email || request.email}`}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium ml-auto"
                    >
                      <Mail className="h-4 w-4 mr-1" />
                      {t('admin.gymRequests.contact')}
                    </a>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {approveRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-5xl bg-gray-50 dark:bg-gray-900 rounded-xl shadow-xl overflow-hidden flex flex-col max-h-[92vh]">
            <div className="shrink-0 px-4 py-3 border-b dark:border-gray-700 flex justify-between items-center gap-3">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('admin.gymRequests.createModalTitle')}</h3>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                  {t('admin.gymRequests.fromRequestBanner', { id: approveRequest.id })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setApproveRequest(null)}
                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 shrink-0"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {createError && (
              <div className="shrink-0 mx-4 mt-3 p-3 bg-red-100 text-red-800 rounded-lg text-sm">{createError}</div>
            )}
            <div className="p-4 overflow-hidden">
              <GymCreateForm
                key={approveRequest.id}
                variant="modal"
                initialValues={requestToInitialValues(approveRequest)}
                onSubmit={async (data, logo) => {
                  try {
                    await handleCreateFromRequest(data, logo);
                  } catch (err) {
                    setCreateError(err.response?.data?.error || t('admin.gymRequests.createError'));
                    throw err;
                  }
                }}
                onCancel={() => setApproveRequest(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GymRequestsManager;
